import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import HorizontalScrollRow from '../General/HorizontalScrollRow';
import DiscoverResult from './DiscoverResult';
import './DiscoverCategoryGrid.scss';

const MAX_CATEGORIES = 5;

const DiscoverCategoryGrid = ({ creators, sortBy, loadMore, loadingMore, noMoreResults, disambiguation, emptyMessage }) => {
  const categories = useMemo(() => {
    // creators is already sorted by the user's chosen sortBy.
    // Group by category in iteration order — first-seen category goes first.
    // Map preserves insertion order, so we get top-down + within-row sorting from a single pass.
    const groups = new Map();
    for (const c of creators) {
      const name = c.topCategoryAffinityName;
      if (!name) continue;
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(c);
    }

    return Array.from(groups.entries())
      .slice(0, MAX_CATEGORIES)
      .map(([name, list]) => ({ name, list }));
  }, [creators]);

  const handleNearEnd = useCallback(() => {
    if (!noMoreResults) loadMore();
  }, [loadMore, noMoreResults]);

  return (
    <div className='discover-category-grid'>
      {categories.length === 0 && (
        <div className='discover-category-empty'>{emptyMessage || 'No scored creators in the current results.'}</div>
      )}
      {categories.map((cat, idx) => (
        <div key={cat.name} className='discover-category-section'>
          <div className='discover-category-header'>
            {cat.name}
            <span className='discover-category-count'>{cat.list.length}</span>
          </div>
          <HorizontalScrollRow contained onNearEnd={idx === categories.length - 1 ? handleNearEnd : undefined}>
            {cat.list.map(c => (
              <div key={c.id} className='discover-category-card-wrap'>
                <DiscoverResult creator={c} sortBy={sortBy} hideTopCategory disambiguation={disambiguation} />
              </div>
            ))}
          </HorizontalScrollRow>
        </div>
      ))}
      {loadingMore && <div className='discover-category-footer'>Loading more…</div>}
      {noMoreResults && creators.length > 0 && (
        <div className='discover-category-footer'>End of results</div>
      )}
    </div>
  );
};

DiscoverCategoryGrid.propTypes = {
  creators: PropTypes.arrayOf(PropTypes.object).isRequired,
  sortBy: PropTypes.string.isRequired,
  loadMore: PropTypes.func.isRequired,
  loadingMore: PropTypes.bool.isRequired,
  noMoreResults: PropTypes.bool.isRequired,
  disambiguation: PropTypes.object,
  emptyMessage: PropTypes.string,
};

export default DiscoverCategoryGrid;
